import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search } from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { getCartCount } = useCart();
  const navigate = useNavigate();

  // Dynamic brand states
  const [watchBrands, setWatchBrands] = useState([]);
  const [wallClockBrands, setWallClockBrands] = useState([]);
  const [accessoryBrands, setAccessoryBrands] = useState({
    'Perfumes': [],
    'Stop Watches': [],
    'Calculators': [],
    'Alarm': []
  });
  const [smartWatchBrands, setSmartWatchBrands] = useState([]);

  const watchCategories = [
    { name: 'Men', path: '/products/Men' },
    { name: 'Women', path: '/products/Women' },
    { name: 'Kids', path: '/products/Kids' },
    { name: 'Unisex', path: '/products/Unisex' },
  ];

  const accessorySubcategories = ['Perfumes', 'Stop Watches', 'Calculators', 'Alarm'];

  // Fetch brands from backend
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        // Fetch brands for each category
        const [watches, wallClocks, smartWatches] = await Promise.all([
          axios.get(`${API}/brands?category=Watches`),
          axios.get(`${API}/brands?category=Wall Clocks`),
          axios.get(`${API}/brands?category=Smart Watches`)
        ]);

        setWatchBrands(watches.data || []);
        setWallClockBrands(wallClocks.data || []);
        setSmartWatchBrands(smartWatches.data || []);

        // Fetch brands for each accessory subcategory
        const accessoryBrandsData = {};
        await Promise.all(
          accessorySubcategories.map(async (subcategory) => {
            try {
              const response = await axios.get(`${API}/brands?category=Accessories&subcategory=${subcategory}`);
              accessoryBrandsData[subcategory] = response.data || [];
            } catch (err) {
              console.error(`Error fetching brands for ${subcategory}:`, err);
              accessoryBrandsData[subcategory] = [];
            }
          })
        );
        setAccessoryBrands(accessoryBrandsData);
      } catch (error) {
        console.error('Error fetching brands:', error);
        // Keep empty arrays if fetch fails
      }
    };

    fetchBrands();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
    }
  };

  return (
    <header className="bg-black text-white sticky top-0 z-50 shadow-lg">
      {/* Top Bar - Compact on mobile */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black py-1.5 sm:py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center text-[10px] sm:text-xs md:text-sm font-medium space-x-2 sm:space-x-4 md:space-x-8">
            <span>✓ Trusted Since 1975</span>
            <span>✓ 40+ Brands</span>
            <span>✓ Ships All India</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-3 sm:px-4 py-2.5 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="text-sm sm:text-xl md:text-2xl lg:text-3xl font-bold whitespace-nowrap">
              <span className="text-yellow-500">JAIN</span>
              <span className="text-white ml-1">WATCH COMPANY</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <Link to="/" className="hover:text-yellow-500 transition-colors font-medium text-sm xl:text-base">
              Home
            </Link>
            
            {/* Brands Dropdown */}
            <div className="relative group">
              <button className="hover:text-yellow-500 transition-colors font-medium text-sm xl:text-base">
                Brands
              </button>
              <div className="absolute left-0 mt-2 w-64 bg-white text-black rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 p-4 z-50">
                {watchBrands.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {watchBrands.map((brand) => (
                      <Link
                        key={brand.id}
                        to={`/products?brand=${brand.name}`}
                        className="block hover:text-yellow-600 py-1 transition-colors text-sm"
                      >
                        {brand.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No brands available</p>
                )}
              </div>
            </div>

            {/* Watches Dropdown */}
            <div className="relative group">
              <button className="hover:text-yellow-500 transition-colors font-medium text-sm xl:text-base">
                Watches
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white text-black rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 p-4 z-50">
                <div className="space-y-2">
                  {watchCategories.map((cat, idx) => (
                    <Link
                      key={idx}
                      to={cat.path}
                      className="block hover:text-yellow-600 py-1 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Wall Clocks Dropdown */}
            <div className="relative group">
              <button className="hover:text-yellow-500 transition-colors font-medium text-sm xl:text-base">
                Wall Clocks
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white text-black rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 p-4 z-50">
                {wallClockBrands.length > 0 ? (
                  <div className="space-y-2">
                    {wallClockBrands.map((brand) => (
                      <Link
                        key={brand.id}
                        to={`/products?category=Wall Clocks&brand=${brand.name}`}
                        className="block hover:text-yellow-600 py-1 transition-colors"
                      >
                        {brand.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No brands available</p>
                )}
              </div>
            </div>

            {/* Accessories Dropdown */}
            <div className="relative group">
              <button className="hover:text-yellow-500 transition-colors font-medium text-sm xl:text-base">
                Accessories
              </button>
              <div className="absolute left-0 mt-2 w-80 bg-white text-black rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 p-4 z-50">
                <div className="space-y-4">
                  {accessorySubcategories.map((subcategory) => (
                    <div key={subcategory} className="border-b border-gray-200 last:border-b-0 pb-3 last:pb-0">
                      <Link
                        to={`/products?category=Accessories&subcategory=${subcategory}`}
                        className="block font-semibold text-yellow-600 hover:text-yellow-700 mb-2"
                      >
                        {subcategory}
                      </Link>
                      {accessoryBrands[subcategory] && accessoryBrands[subcategory].length > 0 ? (
                        <div className="grid grid-cols-2 gap-2 pl-3">
                          {accessoryBrands[subcategory].map((brand) => (
                            <Link
                              key={brand.id}
                              to={`/products?category=Accessories&subcategory=${subcategory}&brand=${brand.name}`}
                              className="block hover:text-yellow-600 py-1 transition-colors text-sm"
                            >
                              {brand.name}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-xs pl-3">No brands yet</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Smart Watches Dropdown */}
            <div className="relative group">
              <button className="hover:text-yellow-500 transition-colors font-medium text-sm xl:text-base">
                Smart Watches
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white text-black rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 p-4 z-50">
                {smartWatchBrands.length > 0 ? (
                  <div className="space-y-2">
                    {smartWatchBrands.map((brand) => (
                      <Link
                        key={brand.id}
                        to={`/products?category=Smart Watches&brand=${brand.name}`}
                        className="block hover:text-yellow-600 py-1 transition-colors"
                      >
                        {brand.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No brands available</p>
                )}
              </div>
            </div>
          </nav>

          {/* Search & Cart */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <form onSubmit={handleSearch} className="hidden md:flex items-center">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-2 py-1.5 lg:px-3 lg:py-1.5 rounded-l-full bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-yellow-500 w-28 lg:w-36 text-xs lg:text-sm"
              />
              <button
                type="submit"
                className="px-2 py-1.5 lg:px-3 lg:py-1.5 bg-yellow-500 text-black rounded-r-full hover:bg-yellow-600 transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>

            <Link to="/cart" className="relative hover:text-yellow-500 transition-colors">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-yellow-500 text-black text-[10px] sm:text-xs font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden hover:text-yellow-500 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="md:hidden mt-2.5 sm:mt-4 flex">
          <input
            type="text"
            placeholder="Search watches..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 rounded-l-full bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-yellow-500 text-sm"
          />
          <button
            type="submit"
            className="px-3 py-2 bg-yellow-500 text-black rounded-r-full hover:bg-yellow-600 transition-colors"
          >
            <Search className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </form>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-gray-900 border-t border-gray-800 max-h-[70vh] overflow-y-auto">
          <nav className="container mx-auto px-4 py-3 sm:py-4 flex flex-col space-y-3 sm:space-y-4">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="hover:text-yellow-500 transition-colors font-medium text-sm sm:text-base"
            >
              Home
            </Link>
            
            {/* Brands */}
            <div className="border-t border-gray-800 pt-2">
              <p className="text-yellow-500 font-medium mb-2 text-sm sm:text-base">Brands</p>
              {watchBrands.length > 0 ? (
                <div className="grid grid-cols-3 gap-1 sm:gap-2 pl-3 sm:pl-4">
                  {watchBrands.map((brand) => (
                    <Link
                      key={brand.id}
                      to={`/products?brand=${brand.name}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 hover:text-yellow-500 transition-colors text-xs sm:text-sm"
                    >
                      {brand.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-xs sm:text-sm pl-3 sm:pl-4">No brands available</p>
              )}
            </div>

            {/* Watches */}
            <div className="border-t border-gray-800 pt-2">
              <p className="text-yellow-500 font-medium mb-2 text-sm sm:text-base">Watches</p>
              <div className="grid grid-cols-2 gap-1 sm:gap-2 pl-3 sm:pl-4">
                {watchCategories.map((cat, idx) => (
                  <Link
                    key={idx}
                    to={cat.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-1 hover:text-yellow-500 transition-colors text-xs sm:text-sm"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Wall Clocks */}
            <div className="border-t border-gray-800 pt-2">
              <p className="text-yellow-500 font-medium mb-2 text-sm sm:text-base">Wall Clocks</p>
              {wallClockBrands.length > 0 ? (
                <div className="grid grid-cols-3 gap-1 sm:gap-2 pl-3 sm:pl-4">
                  {wallClockBrands.map((brand) => (
                    <Link
                      key={brand.id}
                      to={`/products?category=Wall Clocks&brand=${brand.name}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 hover:text-yellow-500 transition-colors text-xs sm:text-sm"
                    >
                      {brand.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-xs sm:text-sm pl-3 sm:pl-4">No brands available</p>
              )}
            </div>

            {/* Accessories */}
            <div className="border-t border-gray-800 pt-2">
              <p className="text-yellow-500 font-medium mb-2 text-sm sm:text-base">Accessories</p>
              <div className="space-y-2 sm:space-y-3 pl-3 sm:pl-4">
                {accessorySubcategories.map((subcategory) => (
                  <div key={subcategory}>
                    <Link
                      to={`/products?category=Accessories&subcategory=${subcategory}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block font-semibold text-yellow-400 hover:text-yellow-500 mb-1 text-xs sm:text-sm"
                    >
                      {subcategory}
                    </Link>
                    {accessoryBrands[subcategory] && accessoryBrands[subcategory].length > 0 ? (
                      <div className="grid grid-cols-3 gap-1 sm:gap-2 pl-2 sm:pl-3">
                        {accessoryBrands[subcategory].map((brand) => (
                          <Link
                            key={brand.id}
                            to={`/products?category=Accessories&subcategory=${subcategory}&brand=${brand.name}`}
                            onClick={() => setMobileMenuOpen(false)}
                            className="py-0.5 hover:text-yellow-500 transition-colors text-[10px] sm:text-xs"
                          >
                            {brand.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-[10px] sm:text-xs pl-2 sm:pl-3">No brands yet</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Smart Watches */}
            <div className="border-t border-gray-800 pt-3 sm:pt-4">
              <p className="text-yellow-500 font-medium mb-2 text-sm sm:text-base">Smart Watches</p>
              {smartWatchBrands.length > 0 ? (
                <div className="grid grid-cols-3 gap-1 sm:gap-2 pl-3 sm:pl-4">
                  {smartWatchBrands.map((brand) => (
                    <Link
                      key={brand.id}
                      to={`/products?category=Smart Watches&brand=${brand.name}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-1 hover:text-yellow-500 transition-colors text-xs sm:text-sm"
                    >
                      {brand.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-xs sm:text-sm pl-3 sm:pl-4">No brands available</p>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
